// =================================================================================================
// >>> **CLIENT-ERROR-BEACON-1 — THE LAWS. HER WORD, 2026-09-05.** <<<
//
// *"the /talk page reports {reached, browser, in-app-browser, storage-blocked, any uncaught error
//  and where} from REAL visitors to a server endpoint, firing before anything can crash, so the
//  next 100 real TikTok arrivals prove whether the storage fix actually holds on real phones."*
//
// >>> **THE SENDER IS A STATIC INLINE SCRIPT IN `index.html` AND CANNOT IMPORT THIS FILE.** <<< It
// has to run BEFORE the module bundle is requested — that is the entire point, because the failure
// being measured is the bundle's first line throwing — and a `<script type="module">` is deferred by
// specification, so it would arrive AFTER the crash it exists to report. The arrangement is
// `ttvTag.js`'s, already used in this house for the same reason: **the snippet carries the smallest
// possible code and the FAST gate holds it against the laws here**, which is the only thing that can
// stop the two drifting when one of them cannot import the other (N20-4).
//
// >>> **SO THE SNIPPET CLASSIFIES NOTHING.** <<< It sends the raw user agent and three raw booleans.
// Every judgement — *is this TikTok*, *did this paint*, *is this row worth reading* — is made HERE,
// once, on rows already in the table. A classifier inside the snippet would be a second answer that
// could only be corrected by a deploy, and rows already collected would keep the old answer for ever
// (R28/R29). Judging on READ means a mistake in this file is fixable over the whole history.
// =================================================================================================

/** How much of a user agent is kept. Long enough to name a webview, short enough to be a fact. */
export const UA_MAX = 400;
/** How many uncaught errors one visit reports. A loop throwing every frame must not post a book. */
export const ERRORS_MAX = 5;
/** How long each error field may be. A stack is truncated, never dropped: WHERE is half the answer. */
export const ERROR_FIELD_MAX = 500;
/** The visit id's exact length, so a forged or truncated one is refused on shape alone. */
export const VISIT_LEN = 24;

const str = (v, max) => String(v == null ? "" : v).slice(0, max);

/**
 * >>> **WHICH IN-APP BROWSER THIS IS, AND EMPTY WHEN IT IS AN ORDINARY ONE.** <<<
 *
 * **TIKTOK IS THE ONE THAT MATTERS AND IT DOES NOT SAY "TIKTOK".** Its iOS webview identifies itself
 * as `musical_ly_<build>` — the app's original name — and its Android one as `trill_<build>`, with
 * `BytedanceWebview` on some builds. **A house that greps for the word "tiktok" finds nothing and
 * concludes it has no TikTok traffic**, which is precisely the shape of not-knowing that cost 21,751
 * arrivals. All three spellings are matched, and the word itself is matched too in case a later
 * build starts using it.
 *
 * The others are here because they are the same CLASS of envelope — a partitioned in-app webview —
 * and telling them apart is what makes "TikTok specifically" a number rather than a guess.
 */
export function inAppBrowser(ua) {
  const s = String(ua || "");
  if (/musical_ly|trill_|BytedanceWebview|TikTok/i.test(s)) return "tiktok";
  if (/Instagram/i.test(s)) return "instagram";
  if (/FBAN|FBAV|FB_IAB/i.test(s)) return "facebook";
  if (/Snapchat/i.test(s)) return "snapchat";
  if (/Pinterest/i.test(s)) return "pinterest";
  if (/MicroMessenger|Twitter/i.test(s)) return "other-in-app";
  return "";
}

/**
 * IT #319 — **CAN A PAGE'S OWN SCRIPT SAVE A FILE HERE?** No inside an app's browser: every one named
 * above, and a bare Android webview (`; wv)` in its agent), drops a script-started download without
 * a word. Read by `setDownloadEnvironment` (src/lib/downloads.ts) once, at boot.
 */
export function cannotSaveFiles(ua) {
  return inAppBrowser(ua) !== "" || /;\s*wv\)/.test(String(ua || ""));
}

/** iOS or Android, for reading a webview's envelope. Empty where neither is claimed. */
export function platformOf(ua) {
  const s = String(ua || "");
  if (/iPhone|iPad|iPod/i.test(s)) return "ios";
  if (/Android/i.test(s)) return "android";
  return "";
}

/**
 * >>> **THE PAYLOAD CONTRACT, AND IT IS A READER RATHER THAN A SHARED TYPE.** <<< A body arrives
 * from a stranger's phone through an open door, so every field is coerced, clamped and capped here
 * before it is anywhere near the database.
 *
 * **ANYTHING IT CANNOT MAKE SENSE OF BECOMES A DEFAULT, NEVER A REFUSAL.** A beacon that answered
 * 400 on a malformed field would throw away exactly the visits that are malformed BECAUSE the page
 * was breaking — the only kind this instrument exists for.
 */
export function readBeacon(body = {}) {
  const b = body && typeof body === "object" ? body : {};
  const errors = (Array.isArray(b.errs) ? b.errs : []).slice(0, ERRORS_MAX).map((e) => ({
    message: str(e?.m, ERROR_FIELD_MAX),
    source: str(e?.s, ERROR_FIELD_MAX),
    line: Number.isFinite(Number(e?.l)) ? Math.trunc(Number(e.l)) : 0,
    col: Number.isFinite(Number(e?.c)) ? Math.trunc(Number(e.c)) : 0,
    stack: str(e?.st, ERROR_FIELD_MAX),
    kind: e?.k === "rejection" ? "rejection" : "error",
  })).filter((e) => e.message || e.stack);
  return {
    visit: str(b.v, VISIT_LEN),
    path: str(b.p, 200),
    ua: str(b.ua, UA_MAX),
    referrer: str(b.r, 300),
    lsBlocked: b.ls === true,
    ssBlocked: b.ss === true,
    idbBlocked: b.idb === true,
    painted: b.painted === true,
    ms: Math.max(0, Math.min(600000, Math.trunc(Number(b.ms)) || 0)),
    arm: ["timer", "pagehide", "hidden", "late"].includes(String(b.why)) ? String(b.why) : "",
    resources: (Array.isArray(b.res) ? b.res : []).slice(0, ERRORS_MAX).map((r) => ({
      tag: str(r?.tag, 12), url: str(r?.url, 300),
    })).filter((r) => r.url),
    rootKids: Math.max(0, Math.min(9999, Math.trunc(Number(b.kids)) || 0)),
    bodyChars: Math.max(0, Math.min(9999999, Math.trunc(Number(b.chars)) || 0)),
    errors,
  };
}

/**
 * **IS THIS ROW WORTH KEEPING?** The ONLY refusal, and it is about SHAPE rather than content: a
 * visit id of the wrong length is not a browser this house served, it is somebody poking the door.
 *
 * Everything else is kept, including a visit that reports nothing wrong at all — *nothing to report*
 * is the answer she is hoping for, and it has to be countable or the rate has no denominator.
 */
export function beaconUsable(read) {
  return Boolean(read) && typeof read.visit === "string"
    && read.visit.length === VISIT_LEN && /^[a-z0-9]+$/i.test(read.visit);
}

/**
 * >>> **THE THREE STATES OF A PAGE LOAD, AND THE THIRD ONE IS WHY THIS FUNCTION EXISTS (R18).** <<<
 *
 * The first real arrival this beacon ever caught was a Samsung on Android 16 referred by TikTok:
 * `painted:false`, `root children=0`, storage NOT blocked, nothing thrown. **That row is unreadable**
 * as it stands, because it is two completely different events wearing identical fields.
 *
 *   `painted`  — React mounted and put something on the screen. The good case.
 *   `blank`    — the timer reached 2.5 seconds and the page still had nothing. **A real blank.**
 *   `left`     — she navigated away before we could tell. Ordinary behaviour on an advertisement,
 *                and calling it a blank page would inflate the very number she is trying to trust.
 *
 * **A ROW WITH NO `arm` AT ALL IS `left`, NOT `blank`** — that is the conservative direction, and it
 * is the one that cannot flatter this house. Every row written before this field existed lands there
 * honestly rather than being counted as evidence in either direction.
 */
export function beaconVerdict(row = {}) {
  if (row?.painted === true) return "painted";
  const arm = String(row?.arm ?? row?.why ?? "");
  return arm === "timer" ? "blank" : "left";
}

/**
 * >>> **THE ANSWER TO HER QUESTION, COMPUTED OVER ROWS RATHER THAN ASSERTED.** <<< *"of real TikTok
 * arrivals, how many now paint the page versus blank, the exact error if any remain."*
 *
 * **THE PAINT RATE IS OVER `painted + blank`, AND NEVER OVER THE ONES WHO LEFT.** A woman who bounced
 * at four hundred milliseconds is evidence about an advertisement, not about a bundle, and including
 * her in the denominator would make a healthy site look broken while including her in the numerator
 * would do the opposite. She is counted, reported, and kept out of the rate — which is the same
 * ruling `paintRate` already carries for zero arrivals (R141): where there is nothing to divide, the
 * answer is nothing rather than a number.
 */
export function beaconRollup(rows = [], { inApp = null } = {}) {
  const kept = (Array.isArray(rows) ? rows : [])
    .filter((r) => (inApp === null ? true : inAppBrowser(r?.ua) === inApp));
  const painted = kept.filter((r) => beaconVerdict(r) === "painted");
  const blank = kept.filter((r) => beaconVerdict(r) === "blank");
  const left = kept.filter((r) => beaconVerdict(r) === "left");
  const withErrors = kept.filter((r) => Array.isArray(r?.errors) && r.errors.length > 0);
  // >>> **A FILE THAT NEVER ARRIVED IS ITS OWN COUNT.** <<< A `<script>` that fails to fetch throws
  // no exception and carries no message, so it was landing in this table as "0 uncaught errors"
  // beside a blank page — the most misleading answer this instrument could give.
  const withResourceFails = kept.filter((r) => Array.isArray(r?.resources) && r.resources.length > 0);
  // Every distinct error message with how many visits hit it, worst first. **The MESSAGE is the key
  // and not the stack**: one defect thrown from three inlined frames is one defect, and counting it
  // three times would put the loudest formatting at the top instead of the commonest fault.
  const byMessage = new Map();
  for (const r of withErrors) {
    for (const e of r.errors) {
      const key = String(e?.message || "").slice(0, 200) || "(no message)";
      const hit = byMessage.get(key) || { message: key, visits: 0, where: "" };
      hit.visits += 1;
      if (!hit.where) {
        hit.where = [e?.source, e?.line, e?.col].filter(Boolean).join(":")
          || String(e?.stack || "").slice(0, 160);
      }
      byMessage.set(key, hit);
    }
  }
  const files = new Map();
  for (const r of withResourceFails) {
    for (const f of r.resources) {
      const key = `${f.tag} ${f.url}`;
      files.set(key, (files.get(key) || 0) + 1);
    }
  }
  const decided = painted.length + blank.length;
  return {
    arrivals: kept.length,
    painted: painted.length,
    blank: blank.length,
    left: left.length,
    // The DENOMINATOR is the loads this house can actually speak about. Zero of them is `null`.
    decided,
    paintRate: decided ? painted.length / decided : null,
    visitsWithErrors: withErrors.length,
    visitsWithResourceFails: withResourceFails.length,
    storageBlocked: kept.filter((r) => r?.ls_blocked === true || r?.lsBlocked === true).length,
    errors: [...byMessage.values()].sort((a, b) => b.visits - a.visits),
    resourceFails: [...files.entries()].map(([file, visits]) => ({ file, visits })).sort((a, b) => b.visits - a.visits),
  };
}
